import type { Faro } from "@grafana/faro-web-sdk";
import type { createBrowserRouter } from "react-router-dom";
import { createPageLifecycle } from "./page-lifecycle";
import { createPrivacyFilter, type Measurement } from "./measurements";

interface AuthenticationObserver {
  isAuthenticated: () => boolean;
  subscribe: (listener: () => void) => unknown;
}

interface Session {
  user?: { id?: string };
  accessToken?: string;
  needsOtp?: boolean;
  needsActivation?: boolean;
}

export const hasAuthenticatedSession = (state: {
  auth?: { sessions?: { sessionData?: Session } };
}) => {
  const session = state.auth?.sessions?.sessionData;
  return Boolean(
    session?.user?.id && session.accessToken && !session.needsOtp && !session.needsActivation
  );
};

// No default browser instrumentations, console, resource timings or replay.
let sdk: Promise<Faro | undefined> | undefined;
let ensureSDK: (() => Promise<Faro | undefined>) | undefined;
let faro: Faro | undefined;
let started = false;
let authenticated = false;
const allowedAttempts = new Set<string>();

async function emit(input: Measurement) {
  if (!authenticated || !ensureSDK) {
    return;
  }

  allowedAttempts.add(input.context.attempt_id);
  try {
    // Once loaded, enqueue synchronously so Faro's visibility-change flush sees the item.
    const client = faro ?? await ensureSDK();
    if (!client || !authenticated || !allowedAttempts.has(input.context.attempt_id)) {
      allowedAttempts.delete(input.context.attempt_id);
      return;
    }
    client.api.pushMeasurement(input, {
      skipDedupe: true,
      context: input.context,
      timestampOverwriteMs: Date.parse(input.timestamp),
    });
  } catch {
    allowedAttempts.delete(input.context.attempt_id);
  }
}

const lifecycle = createPageLifecycle(emit);

export const pageLifecycle = {
  reduxLoading: lifecycle.reduxLoading,
  routeName: (...args: Parameters<typeof lifecycle.routeName>) => {
    if (authenticated) {
      lifecycle.routeName(...args);
    }
  },
  signal: (...args: Parameters<typeof lifecycle.signal>) => {
    if (authenticated) {
      lifecycle.signal(...args);
    }
  },
  commit: () => {
    if (authenticated) {
      lifecycle.commit();
    }
  },
};

export const observeNavigation = (router: ReturnType<typeof createBrowserRouter>) => {
  if (!import.meta.env.VITE_FARO_ENDPOINT) {
    return;
  }

  let lastKey: string | undefined;
  const sync = () => {
    const { location, historyAction } = router.state;
    if (location.key === lastKey) {
      return;
    }
    lastKey = location.key;
    if (authenticated) {
      lifecycle.navigate(
        new URL(router.createHref(location), window.location.origin).href,
        false,
        historyAction === "REPLACE"
      );
    }
  };
  sync();
  return router.subscribe(sync);
};

export const initializeNavigationPilot = (auth: AuthenticationObserver) => {
  const endpoint = import.meta.env.VITE_FARO_ENDPOINT;
  if (started || !endpoint || typeof globalThis.crypto?.randomUUID !== "function") {
    return;
  }

  started = true;

  document.addEventListener("visibilitychange", () => {
    if (authenticated) {
      lifecycle.visibility(document.hidden);
    }
  });
  let warnedAboutSDK = false;
  ensureSDK = () => {
    if (sdk) {
      return sdk;
    }
    // Navigation runs immediately; only delivery waits for the SDK chunk.
    sdk = import("@grafana/faro-web-sdk").then(({ initializeFaro }) => {
      const mode = import.meta.env.MODE;
      const environment = mode === "telepresence" || mode === "development" ? "local" : mode;
      const version = String(import.meta.env.VITE_VENDOR_WEB_BUILD_VERSION || "unknown");
      const app = { name: "vendor-web", environment, version };
      const filterMeasurement = createPrivacyFilter(environment, version);
      faro = initializeFaro({
        url: endpoint,
        apiKey: import.meta.env.VITE_FARO_API_KEY || undefined,
        app,
        instrumentations: [],
        beforeSend: (item) => {
          if (!authenticated) {
            return null;
          }
          const safe = filterMeasurement(item);
          if (!safe || !allowedAttempts.delete(safe.payload.context.attempt_id)) {
            return null;
          }
          return safe;
        },
      });
      return faro;
    }).catch(() => {
      sdk = undefined;
      if (!warnedAboutSDK) {
        warnedAboutSDK = true;
        console.warn("Faro SDK could not load; a later page measurement will retry.");
      }
      return undefined;
    });
    return sdk;
  };
  const syncAuth = (initial = false) => {
    const next = auth.isAuthenticated();
    if (next === authenticated) {
      return;
    }
    authenticated = next;
    // Drop unfinished attempts and queued SDK items across logout/login boundaries.
    allowedAttempts.clear();
    lifecycle.reset();
    if (authenticated) {
      lifecycle.visibility(document.hidden);
      ensureSDK?.();
      lifecycle.navigate(window.location.href, initial);
    }
  };

  auth.subscribe(() => syncAuth());
  syncAuth(true);
};
